// Domain Feed Service - Real API integration for Domain Feed feed operations
// Connects to backend Express API for Domain Feed domain feed management

import { apiClient } from './apiClient';

export interface DomainFeedItem {
  domain: string;
  organization: string;
  region: string;
  riskLevel: 'low' | 'medium' | 'high';
  lastUpdated: string;
  isActive: boolean;
}

export interface DomainFeedStatus {
  status: 'healthy' | 'warning' | 'error';
  lastUpdate: string;
  responseTime: number;
  domainsCount?: number;
  message?: string;
}

export interface DomainFeedSyncResult {
  success: boolean;
  domainsProcessed: number;
  domainsAdded: number;
  domainsUpdated: number;
  domainsSkipped?: number;
  domainsRemoved: number;
  hasChanges?: boolean;
  message?: string;
  approvedDomainsToRemove?: string[];
  changes?: {
    added: string[];
    updated: string[];
    removed: string[];
  };
  errors: string[];
  timestamp: string;
}

class DomainFeedService {
  // Get Domain Feed feed data via backend API (updated)
  async getFeedData(feedType: string = 'banking'): Promise<DomainFeedItem[]> {
    try {
      console.log('🔄 Fetching Domain Feed feed data from backend...');
      const response = await apiClient.get<{
        domains: { domain: string; organization: string; dateAdded: string }[];
        count: number;
        lastUpdated: string;
      }>('/lseg/domains');

      console.log('📦 Raw response from apiClient:', response);
      console.log('📦 Response.data:', response.data);
      
      // The apiClient returns ApiResponse<T> which has { success: boolean, data: T }
      // So response.data contains our actual data
      const feedData = response.data;
      return this.transformBackendLSEGData(feedData);
    } catch (error) {
      console.error('❌ Failed to fetch Domain Feed feed from backend:', error);
      throw new Error('Failed to load Domain Feed banking domain feed. Please try again.');
    }
  }

  // Transform backend Domain Feed data format
  private transformBackendLSEGData(backendData: any): DomainFeedItem[] {
    console.log('🔍 Backend Domain Feed data:', backendData);
    
    // Handle different response structures
    // The backend returns { domains: [...], count: N, lastUpdated: "...", source: "..." }
    const domains = backendData?.domains || [];
    
    console.log('🔍 Backend Domain Feed domains array:', domains);
    console.log('🔍 Number of domains:', domains.length);
    
    return domains.map((item: { domain: string; organization: string; dateAdded?: string }) => ({
      domain: item.domain,
      organization: item.organization || this.inferOrganizationFromDomain(item.domain),
      region: this.inferRegionFromDomain(item.domain),
      riskLevel: this.inferRiskLevel(item.domain),
      lastUpdated: item.dateAdded || new Date().toISOString(),
      isActive: true // All domains from Domain Feed feed are active
    }));
  }

  // Helper methods for domain inference
  private inferOrganizationFromDomain(domain: string): string {
    const cleanDomain = domain.replace(/^(www\.|mail\.|mx\.)/, '').replace(/\.(com|org|net|gov|edu|co\.uk|co\.jp)$/, '');
    return cleanDomain.charAt(0).toUpperCase() + cleanDomain.slice(1).replace(/-/g, ' ');
  }

  private inferRegionFromDomain(domain: string): string {
    const tldMap: Record<string, string> = {
      '.uk': 'UK', '.co.uk': 'UK', '.de': 'DE', '.fr': 'FR', '.jp': 'JP',
      '.co.jp': 'JP', '.au': 'AU', '.ca': 'CA', '.ch': 'CH', '.nl': 'NL', '.se': 'SE'
    };

    for (const [tld, region] of Object.entries(tldMap)) {
      if (domain.endsWith(tld)) return region;
    }
    return 'US'; // Default to US for .com domains
  }

  private inferRiskLevel(domain: string): 'low' | 'medium' | 'high' {
    const region = this.inferRegionFromDomain(domain);
    const highRiskRegions = ['CN', 'RU', 'IR', 'KP', 'BY'];
    const lowRiskRegions = ['US', 'UK', 'DE', 'FR', 'JP', 'CA', 'AU', 'CH', 'SE', 'NL'];
    
    if (highRiskRegions.includes(region)) return 'high';
    if (lowRiskRegions.includes(region)) return 'low';
    return 'medium';
  }

  // Get Domain Feed feed health status via backend
  async getHealthStatus(): Promise<DomainFeedStatus> {
    try {
      const response = await apiClient.get<{
        success: boolean;
        data: {
          status: 'healthy' | 'degraded' | 'unhealthy';
          lastUpdate: string;
          responseTime: number;
          feedEnabled: boolean;
          domainsCached: number;
        };
      }>('/lseg/health');

      const healthData = response.data.data;
      return {
        status: healthData.status === 'healthy' ? 'healthy' : 
                healthData.status === 'degraded' ? 'warning' : 'error',
        lastUpdate: healthData.lastUpdate,
        responseTime: healthData.responseTime,
        domainsCount: healthData.domainsCached
      };
    } catch (error) {
      console.error('Failed to fetch Domain Feed health status from backend:', error);
      return {
        status: 'error',
        lastUpdate: new Date().toISOString(),
        responseTime: 0,
        message: error instanceof Error ? error.message : 'Connection failed'
      };
    }
  }

  // Manually trigger Domain Feed feed sync - loads Domain Feed domains into local storage
  async triggerSync(): Promise<DomainFeedSyncResult> {
    try {
      // Sync Domain Feed domains into local domain storage 
      const response = await apiClient.post<{
        success: boolean;
        data: {
          message: string;
          domainsProcessed: number;
          domainsAdded: number;
          domainsUpdated: number;
          domainsAlreadyApproved: number;
          domainsPending: number;
          errors: string[];
        };
      }>('/domains/sync-lseg', {});

      // Handle both response structures (with and without data wrapper)
      const result = response.data?.data || response.data || response;
      
      return {
        success: true, // If we got here, the sync was successful
        domainsProcessed: result.domainsProcessed || 0,
        domainsAdded: result.domainsAdded || 0,
        domainsUpdated: result.domainsUpdated || 0,
        domainsSkipped: (result.domainsAlreadyApproved || 0) + (result.domainsPending || 0),
        domainsRemoved: 0, // Domain feed doesn't remove domains
        errors: result.errors || [],
        timestamp: new Date().toISOString()
      };
    } catch (error) {
      // Silently handle error and throw user-friendly message
      throw new Error('Failed to sync domain feed. Please try again.');
    }
  }

  // Get sync history/status
  async getSyncStatus(): Promise<{
    lastSync: string;
    status: 'idle' | 'syncing' | 'success' | 'error';
    nextSync?: string;
    message?: string;
  }> {
    try {
      const response = await apiClient.get('/lseg/sync-status');
      return response.data as { lastSync: string; status: 'idle' | 'syncing' | 'success' | 'error'; nextSync?: string; message?: string; };
    } catch (error) {
      // No fallbacks - throw the error
      throw new Error('Failed to get sync status. Please try again.');
    }
  }
}

export const domainFeedService = new DomainFeedService();